import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../sectepe-ui/index';
import replyService, { ClearingFall } from '../services/replyService';
import tenantService, { Tenant } from '../services/tenant.service';
import { PageHeader, EmptyState, LoadingState, StatusBadge } from '../components/ui';
import { Icon } from '../components/icons';
import { datum, fehlerText } from '../vertrieb/format';

/**
 * Clearing-Stelle fuer Lemlist-Antworten ohne Mandant.
 *
 * Lemlist laeuft ueber einen installationsweiten Schluessel. Eine Antwort
 * gehoert dem Mandanten, dessen Kampagne oder Lead sie zuzuordnen ist; was
 * sich nicht eindeutig zuordnen laesst, sieht kein Mandant. Der
 * Plattform-Admin (admin.mandant.alle) weist es hier zu -- oder laesst die
 * Zuordnung erneut laufen, nachdem z. B. eine Kampagne angelegt wurde.
 */
const AntwortenClearingPage: React.FC = () => {
  const { push } = useToast();
  const [faelle, setFaelle] = useState<ClearingFall[]>([]);
  const [mandanten, setMandanten] = useState<Tenant[]>([]);
  const [auswahl, setAuswahl] = useState<Record<string, string>>({});
  const [laedt, setLaedt] = useState(true);
  const [arbeitet, setArbeitet] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      const [liste, alle] = await Promise.all([replyService.getClearing(), tenantService.listTenants()]);
      const sicher = Array.isArray(liste) ? liste : [];
      setFaelle(sicher);
      setMandanten(Array.isArray(alle) ? alle : []);
      // Eindeutiger Vorschlag ist gleich vorausgewaehlt.
      setAuswahl((alt) => {
        const neu = { ...alt };
        for (const f of sicher) if (!neu[f.id] && f.kandidaten.length === 1) neu[f.id] = f.kandidaten[0].tenantId;
        return neu;
      });
      setFehler(null);
    } catch (e) {
      setFehler(fehlerText(e, 'Das Clearing konnte nicht geladen werden'));
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  const zuweisen = async (fall: ClearingFall) => {
    const tenantId = auswahl[fall.id];
    if (!tenantId) return;
    setArbeitet(fall.id);
    try {
      await replyService.clearingZuweisen(fall.id, tenantId);
      const name = mandanten.find((m) => m.id === tenantId)?.name ?? fall.kandidaten.find((k) => k.tenantId === tenantId)?.name;
      push({ message: `Antwort ${name ? `„${name}“` : 'dem Mandanten'} zugewiesen.`, severity: 'success' });
      setFaelle((alt) => alt.filter((f) => f.id !== fall.id));
    } catch (e) {
      setFehler(fehlerText(e, 'Zuweisen fehlgeschlagen'));
    } finally {
      setArbeitet(null);
    }
  };

  const automatisch = async () => {
    setArbeitet('auto');
    try {
      const r = await replyService.clearingAutomatisch();
      push({
        message: r.zugeordnet
          ? `${r.zugeordnet} von ${r.geprueft} Antworten automatisch zugeordnet.`
          : `Keine weitere Antwort eindeutig zuzuordnen (${r.geprueft} geprüft).`,
        severity: r.zugeordnet ? 'success' : 'info',
      });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Automatische Zuordnung fehlgeschlagen'));
    } finally {
      setArbeitet(null);
    }
  };

  if (laedt) {
    return (
      <div className="page-container-wide">
        <LoadingState rows={4} label="Clearing wird geladen" />
      </div>
    );
  }

  return (
    <div className="page-container-wide">
      <PageHeader
        eyebrow="Verwaltung · Plattform"
        title="Antworten-Clearing"
        subtitle="Lemlist-Antworten, die sich keinem Mandanten zuordnen lassen. Kein Mandant sieht sie, bis sie hier zugewiesen sind."
        actions={(
          <button type="button" className="btn btn-secondary" onClick={automatisch} disabled={arbeitet !== null}>
            <Icon name="refresh" size={14} /> {arbeitet === 'auto' ? 'Ordnet zu…' : 'Automatisch erneut zuordnen'}
          </button>
        )}
      />

      {fehler && <div className="alert alert-error" role="alert">{fehler}</div>}

      {faelle.length === 0 ? (
        <EmptyState
          icon="check"
          title="Nichts im Clearing"
          description="Alle eingehenden Lemlist-Antworten sind einem Mandanten zugeordnet."
          action={{ label: 'Zu den Antworten', to: '/reply-handler' }}
        />
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Eingang</th>
                <th>Absender</th>
                <th>Kampagne</th>
                <th>Antwort</th>
                <th>Vorschlag</th>
                <th>Mandant</th>
              </tr>
            </thead>
            <tbody>
              {faelle.map((f) => {
                const name = [f.firstName, f.lastName].filter(Boolean).join(' ');
                return (
                  <tr key={f.id} data-focus-id={f.id}>
                    <td className="text-sm">{datum(f.receivedAt)}{f.source === 'test' && <div><StatusBadge tone="neutral">Test</StatusBadge></div>}</td>
                    <td>
                      <strong>{name || f.email || 'Unbekannt'}</strong>
                      {name && f.email && <div className="text-xs text-secondary">{f.email}</div>}
                      {f.companyName && <div className="text-xs text-secondary">{f.companyName}</div>}
                    </td>
                    <td className="text-sm">
                      {f.campaignName || <span className="text-muted">ohne Namen</span>}
                      {f.lemlistCampaignId && <div className="text-xs text-muted"><code>{f.lemlistCampaignId}</code></div>}
                    </td>
                    <td className="text-sm" style={{ maxWidth: 320 }}>
                      <span title={f.text || ''}>{(f.text || '—').slice(0, 160)}{(f.text || '').length > 160 ? '…' : ''}</span>
                    </td>
                    <td className="text-sm">
                      {f.kandidaten.length === 0
                        ? <span className="text-muted">keiner gefunden</span>
                        : f.kandidaten.map((k) => (
                          <div key={k.tenantId}>
                            <strong>{k.name}</strong>
                            <div className="text-xs text-secondary">{k.grund}</div>
                          </div>
                        ))}
                      {f.kandidaten.length > 1 && <StatusBadge tone="warning">mehrdeutig</StatusBadge>}
                    </td>
                    <td>
                      <div className="btn-group">
                        <select
                          className="form-select select-auto"
                          aria-label={`Mandant für ${name || f.email || 'Antwort'}`}
                          value={auswahl[f.id] || ''}
                          onChange={(e) => setAuswahl((a) => ({ ...a, [f.id]: e.target.value }))}
                        >
                          <option value="">– wählen –</option>
                          {mandanten.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          disabled={!auswahl[f.id] || arbeitet !== null}
                          onClick={() => zuweisen(f)}
                        >
                          {arbeitet === f.id ? 'Weist zu…' : 'Zuweisen'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-sm text-secondary mt-4">
        Zugeordnet wird beim Eingang über die Lemlist-Kampagne (ICP-, Signal-, Sequenz- oder Firmen-Mailing-Kampagne) und ersatzweise über den Lead.
        Eine Antwort landet hier, wenn nichts passt oder mehrere Mandanten in Frage kommen. Nach dem Zuweisen erscheint sie
        beim Mandanten unter <Link to="/reply-handler">Antworten einordnen</Link>.
      </p>
    </div>
  );
};

export default AntwortenClearingPage;
